import React, { FC } from 'react';
import { useTranslation } from 'react-i18next';
import {
    Box,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
} from '@mui/material';
import { OrgEmailNotifications, OrgGeneralNotifications } from 'models/organization';

type SystemSetting = 'priceRequests' | 'invoiceBatches';

// DD-3633: notifications that are not about an order, listed under "System notifications".
// The defaults mirror shared-denthub-code's shouldNotifyOrganization /
// shouldEmailOrganization: price-request emails were always sent before the switch existed.
const SYSTEM_SETTINGS: {
    name: SystemSetting;
    label: string;
    inApplicationDefault: boolean;
    emailDefault: boolean;
}[] = [
    { name: 'priceRequests', label: 'SYSTEM_PRICE_REQUESTS', inApplicationDefault: true, emailDefault: true },
    { name: 'invoiceBatches', label: 'SYSTEM_INVOICE_BATCHES', inApplicationDefault: true, emailDefault: false },
];

type SystemNotificationSettingTableProps = {
    generalNotifications: OrgGeneralNotifications;
    emailNotifications: OrgEmailNotifications;
    handleGeneralNotifications: (event: React.ChangeEvent<HTMLInputElement>) => void;
    handleEmailNotifications: (event: React.ChangeEvent<HTMLInputElement>) => void;
};

const SystemNotificationSettingTable: FC<SystemNotificationSettingTableProps> = ({
    generalNotifications,
    emailNotifications,
    handleGeneralNotifications,
    handleEmailNotifications,
}: SystemNotificationSettingTableProps) => {
    const { t: translate } = useTranslation(['lab-settings', 'common']);

    return (
        <Box mb={4} data-test="system-notification-settings">
            <Typography variant="h3">{translate('lab-settings:SYSTEM_NOTIFICATIONS')}</Typography>
            <Typography variant="body2" color="text.secondary">
                {translate('lab-settings:SYSTEM_NOTIFICATIONS_INFO')}
            </Typography>
            <TableContainer>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell width="60%">{translate('lab-settings:NOTIFICATION')}</TableCell>
                            <TableCell>{translate('lab-settings:IN_APPLICATION')}</TableCell>
                            <TableCell>{translate('common:EMAIL')}</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {SYSTEM_SETTINGS.map(({ name, label, inApplicationDefault, emailDefault }) => (
                            <TableRow key={name}>
                                <TableCell>{translate(`lab-settings:${label}`)}</TableCell>
                                <TableCell>
                                    <Switch
                                        checked={generalNotifications[name] ?? inApplicationDefault}
                                        onChange={handleGeneralNotifications}
                                        name={name}
                                    />
                                </TableCell>
                                <TableCell>
                                    <Switch
                                        checked={emailNotifications[name] ?? emailDefault}
                                        onChange={handleEmailNotifications}
                                        name={name}
                                    />
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );
};

export default SystemNotificationSettingTable;
